<template>
  <div class="login-overlay" @click.self="$emit('close')">
    <div class="profile-card">

      <div class="login-header">
        <h2>{{ t('profile') }}</h2>
        <button class="close-btn" @click="$emit('close')">&#10005;</button>
      </div>

      <div class="profile-body">

        <div class="profile-image-section">
          <img
              class="profile-image"
              :src="imageSrc"
              @error="onImageError"
          />
          <label class="file-label">
            <input
                ref="fileInput"
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                @change="onFileSelected"
            />
            {{ selectedFile ? selectedFile.name : t('selectImage') }}
          </label>
          <button
              v-if="selectedFile"
              :disabled="uploading"
              @click="upload"
          >
            {{ uploading ? t('loading') : t('uploadImage') }}
          </button>
          <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
          <div v-if="successMessage" class="success-message">{{ successMessage }}</div>
        </div>

        <div class="profile-info">
          <div class="profile-row">
            <span class="label">{{ t('email') }}</span>
            <span class="value">{{ session?.username }}</span>
          </div>
          <div v-if="session?.domainAccountName" class="profile-row">
            <span class="label">{{ t('domainAccount') }}</span>
            <span class="value">{{ session.domainAccountName }}</span>
          </div>
        </div>

        <ChatProfileForm
            v-if="connectionId && service"
            :service="service"
            :connectionId="connectionId"
            :showAdminFields="false"
        />

      </div>

      <div class="profile-footer">
        <button class="secondary-btn" @click="$emit('close')">{{ t('close') }}</button>
      </div>

    </div>
  </div>
</template>

<script>
import { useI18n } from "../i18n.js"
import ChatProfileForm from "./ChatProfileForm.vue"

export default {
  name: "ChatProfile",

  components: { ChatProfileForm },

  props: {
    session: { type: Object, required: true },
    imageBaseUrl: { type: String, default: '/tt/api/image/' },
    service: { type: Object, default: null },
    connectionId: { type: [String, Number], default: null }
  },

  emits: ['close', 'image-updated'],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      selectedFile: null,
      uploading: false,
      errorMessage: '',
      successMessage: '',
      imageVersion: 0,
      previewUrl: ''
    }
  },

  computed: {
    imageSrc() {
      if (this.previewUrl) return this.previewUrl
      const base = this.imageBaseUrl || '/tt/api/image/'
      const sep = base.includes('?') ? '&' : '?'
      return `${base}${sep}v=${this.imageVersion}`
    }
  },

  methods: {
    onFileSelected(event) {
      const file = event.target.files?.[0]
      if (!file) return
      this.selectedFile = file
      this.errorMessage = ''
      this.successMessage = ''
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
      this.previewUrl = URL.createObjectURL(file)
    },

    async upload() {
      if (!this.selectedFile || !this.service) return
      this.uploading = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        await this.service.updateUserImage(this.selectedFile)
        this.successMessage = this.t('imageUploaded')
        this.selectedFile = null
        if (this.previewUrl) {
          URL.revokeObjectURL(this.previewUrl)
          this.previewUrl = ''
        }
        if (this.$refs.fileInput) this.$refs.fileInput.value = ''
        this.imageVersion = Date.now()
        this.$emit('image-updated')
      } catch (e) {
        this.errorMessage = e.message || this.t('imageUploadFailed')
      } finally {
        this.uploading = false
      }
    },

    onImageError(ev) {
      ev.target.style.visibility = 'hidden'
    }
  },

  beforeUnmount() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
  }
}
</script>

<style scoped>
.login-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.profile-card {
  width: min(520px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px);
  background: white;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.login-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e4e8ee;
}

.login-header h2 { margin: 0; font-size: 18px; }

.close-btn {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}
.close-btn:hover { color: #000; }

.profile-body {
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.profile-image-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.profile-image {
  width: 160px;
  height: 160px;
  border-radius: 50%;
  object-fit: cover;
  background: #e0e7ff;
  border: 1px solid #e4e8ee;
}

.file-label {
  display: inline-block;
  padding: 8px 14px;
  background: #f0f2f5;
  color: #1a1a2e;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

.file-label:hover { background: #e4e8ee; }

.file-label input[type="file"] { display: none; }

button {
  padding: 10px 14px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
}

button:disabled {
  background: #c7c9d1;
  cursor: not-allowed;
}

.profile-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  background: #f7f8fa;
  border-radius: 8px;
}

.profile-row {
  display: flex;
  gap: 10px;
  font-size: 14px;
}

.profile-row .label {
  min-width: 120px;
  color: #5f6b7a;
  font-weight: 500;
}

.profile-row .value {
  color: #1a1a2e;
  word-break: break-all;
}

.profile-footer {
  padding: 12px 20px;
  border-top: 1px solid #e4e8ee;
  display: flex;
  justify-content: flex-end;
}

.secondary-btn {
  background: #f0f2f5;
  color: #1a1a2e;
}

.secondary-btn:hover { background: #e4e8ee; }

.info-message {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 12px;
}

.error-message {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 12px;
}

.success-message {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 12px;
}
</style>
